"use client";
import type { IResource } from "@gettactic/api";
import React from "react";

export interface RoomSelectionState {
  /** Every room held by the reservation, in pick order — including the one the reserve
   *  card is currently showing. The order is what the map numbers the rooms by, so it
   *  must not change when the card moves from one room to another. */
  rooms: IResource[];
  /** True while the map is armed to add the next room. */
  isPickingRoom: boolean;
  /** True from the moment a second room is taken until the selection is cleared. */
  isMultiRoomSession: boolean;
  /** False once the selection reached MULTI_ROOM_RESERVATION_MAX_ROOMS. */
  canAddRoom: boolean;
  addRoom: (room: IResource) => void;
  removeRoom: (roomId: string) => void;
  startPicking: () => void;
  cancelPicking: () => void;
  clearRooms: () => void;
}

/**
 * Default value rather than `undefined`: the map components that read this context are
 * also rendered on screens without a ReserveProvider (kiosk, wayfinder), where an inert
 * empty selection is the correct behaviour instead of a crash.
 */
export const emptyRoomSelection: RoomSelectionState = {
  rooms: [],
  isPickingRoom: false,
  isMultiRoomSession: false,
  canAddRoom: false,
  addRoom: () => undefined,
  removeRoom: () => undefined,
  startPicking: () => undefined,
  cancelPicking: () => undefined,
  clearRooms: () => undefined
};

export const RoomSelectionContext =
  React.createContext<RoomSelectionState>(emptyRoomSelection);

export const useRoomSelection = (): RoomSelectionState =>
  React.useContext(RoomSelectionContext);
